import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'; // Import FormProvider
import { DropdownWithDeletedValueTemplate } from 'utils/DropdownWithDeletedValueTemplate';

import { ProcessingWorkOrdersSchema } from '../constants/schemas';
import { StatusTransitionsTemplate } from '../constants/StatusTransitionsTemplate';
import useTaskProcessingProcessService from '../hooks/services/useTaskProcessingProcessService';
import {
  DropdownSelectorType,
  Employee,
  FieldFilling,
  RoleUI
} from '../types/interfaces';
import { ObjectForSelection } from '../types/interfaces/ObjectForSelection';
import { TaskProcessingProcess } from '../types/interfaces/TaskProcessingProcess';
import { processingWorkOrdersProps } from '../types/props';
import { generateGUID } from '../utils';

import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';
import { TaskStatusComponent } from './TaskStatusComponent';

import '../ui/css/EmployeeCard.css';
import './css/ProcessingWorkOrdersConstructor.css';

interface RefInterface {
  handleCancel: () => void;
}

const ProcessingWorkOrdersConstructor = React.forwardRef<
  RefInterface | null,
  processingWorkOrdersProps
>(
  (
    {
      businessProcessProps,
      onAdd,
      onUpdate,
      onDelete,
      closeDialog,
      withoutButtons
    }: processingWorkOrdersProps,
    ref
  ) => {
    const methods = useForm({
      resolver: zodResolver(ProcessingWorkOrdersSchema),
      defaultValues: businessProcessProps
    });

    const {
      register,
      handleSubmit,
      watch,
      reset,
      setError,
      setValue,
      trigger, // Get trigger function from useForm
      formState: { errors }
    } = methods;

    const [selectedRoles, setSelectedRoles] = useState<(RoleUI | Employee)[]>(
      []
    );
    const [selectedClosingTaskStatuses, setSelectedClosingTaskStatuses] = useState<ObjectForSelection | null>(null)
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [statuses, setStatuses] = useState<DropdownSelectorType[]>([]);
    const toast = useRef<Toast>(null);

    const name = watch('name');
    const id = watch('id');
    const statusesTransitions = watch('statusesTransitions');

    const taskProcessingProcessService = useTaskProcessingProcessService();
    useEffect(() => {
      const serviceResult = taskProcessingProcessService.getWithTaskStatuses(
        businessProcessProps.id
          ? parseInt(businessProcessProps.id.toString())
          : undefined
      );
      serviceResult.result.then((r) => {
        if (r.taskProcessingProcess) {
          reset(r.taskProcessingProcess);
          if (r.taskProcessingProcess.closingTaskStatuses && r.taskProcessingProcess.closingTaskStatuses.length > 0) {
            setSelectedClosingTaskStatuses(r.taskProcessingProcess.closingTaskStatuses[0]);
          } else {
            setSelectedClosingTaskStatuses(null);
          }
        }
        if (r.taskStatuses) setStatuses(r.taskStatuses);
      });

      return () => {
        if (serviceResult) serviceResult.cancel();
      };
    }, [businessProcessProps.id, reset, taskProcessingProcessService]);

    const onSubmit: SubmitHandler<TaskProcessingProcess> = async (data) => {
      const newData = { ...data };
      
      if (selectedClosingTaskStatuses) {
        newData.closingTaskStatuses = [{
          id: selectedClosingTaskStatuses.id,
          label: selectedClosingTaskStatuses.label
        }];
      } else {
        newData.closingTaskStatuses = [];
      }
      

      if (selectedClosingTaskStatuses && newData.statusesTransitions) {
        const closingStatusId = selectedClosingTaskStatuses.id;
        const hasMatchingStatus = newData.statusesTransitions.some(transition => 
          (transition.fromTaskStatus?.id === closingStatusId) || 
          (transition.toTaskStatus?.id === closingStatusId)
        );
        
        if (!hasMatchingStatus) {
          if (toast.current) {
            toast.current.show({
              severity: 'error',
              summary: 'Ошибка сохранения',
              detail: 'Выбранный закрывающий статус отсутствует в процессе обработки наряда',
              life: 3000
            });
          }
          return;
        }
      }
      
      if (newData.statusesTransitions) {
        newData.statusesTransitions = newData.statusesTransitions.map(
          (x, i) => ({
            ...x,
            fromTaskStatus:
              x.fromTaskStatus?.id === 0 ? undefined : x.fromTaskStatus,
            toTaskStatus: x.toTaskStatus?.id === 0 ? undefined : x.toTaskStatus,
            fieldFillingControls:
              statusesTransitions[i]?.fieldFillingControls ?? [],
            visibleOnMap: x.visibleOnMap
          })
        ) as any;
      }
      
      let result: number | undefined = 0;
      if (onUpdate && data.id) {
        result = await onUpdate(data.id, newData);
      }
      if (onAdd && !data.id) {
        newData.id = 0;
        newData.statusesTransitions.forEach((x) => {
          x.id = 0;
        });
        result = await onAdd(newData);
      }
      if (result === -1) {
        setError('name', {
          type: 'value',
          message:
            'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название'
        });
      } else {
        closeDialog && closeDialog();
      }
    };

    const onError = useCallback((errors: any) => {
      if (toast.current && errors) {
        let errorMessage = '';
        
        if (errors.statusesTransitions) {
          errorMessage = 'Заполните переходы статусов';
        } else if (errors.closingTaskStatuses) {
          errorMessage = errors.closingTaskStatuses.message || 'Выберите закрывающий статус';
        } else if (errors.name) {
          errorMessage = errors.name.message || 'Проверьте название процесса';
        } else {
          errorMessage = 'Проверьте корректность заполнения полей';
        }
        
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка сохранения',
          detail: errorMessage,
          life: 3000
        });
      }
    }, []);

    const handleSelectionChange = (selection: RoleUI[] | Employee[]) => {
      setSelectedRoles(selection);
    };

    const handleClosingStatusChange = async (e: { value: ObjectForSelection | null }) => {
      setUnsavedChanges(true);
      const selectedValue = e.value;
      setSelectedClosingTaskStatuses(selectedValue);
      
      if (selectedValue) {
        setValue('closingTaskStatuses', [selectedValue]);
      } else {
        setValue('closingTaskStatuses', []);
      }
      
      await trigger('closingTaskStatuses');
    };

    const handleCancel = () => {
      if (unsavedChanges) {
        setShowDialog(true);
      }
      if (closeDialog && !unsavedChanges) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
      // Perform navigation/close the card without saving
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = () => {
      if (onDelete) {
        onDelete(id);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    type TaskStatusField =
      | 'id'
      | 'name'
      | 'priority'
      | 'fromTaskStatus'
      | 'toTaskStatus'
      | 'visibleOnMap';;

    const handleInputChange = async (
      field:
        | `statusesTransitions.${number}.${TaskStatusField}`
        | 'name'
        | 'id'
        | 'description',
      value: any,
      index: number
    ) => {
      setUnsavedChanges(true);
      setValue(field, value);
      await trigger(field);
    };

    const handleTaskStatusUpdate = (
      taskId: string | number,
      updatedData: FieldFilling[]
    ) => {
      const index = statusesTransitions.findIndex(
        (statusesTransitions) => statusesTransitions.id === taskId
      );
      setUnsavedChanges(true);
      // Do something with the selected value, e.g., update the form data
      setValue(
        `statusesTransitions.${index}.fieldFillingControls`,
        updatedData || []
      );
    };

    const handleCurrentInputChange = () => {
      setUnsavedChanges(true);
    };

    const handleAddTaskStatus = async () => {
      // Add a new empty task status object to the form data
      const currentTaskStatuses = statusesTransitions || [];
      const newTaskStatus = StatusTransitionsTemplate;
      newTaskStatus.guid = generateGUID();

      setValue('statusesTransitions', [...currentTaskStatuses, newTaskStatus]);
      setUnsavedChanges(true);

      // Trigger validation after adding a new task status
      await trigger('statusesTransitions');
    };

    const handleDropdownChange = async (
      selectedValue: DropdownSelectorType | null,
      property: 'fromTaskStatus' | 'toTaskStatus',
      index: number
    ) => {
      setUnsavedChanges(true);

      // Allow null values to clear the selection
      if (selectedValue) {
        const newSelectedValue: ObjectForSelection = {
          id: parseInt(selectedValue.id?.toString() ?? '0'),
          label: selectedValue.label ?? ''
        };
        setValue(`statusesTransitions.${index}.${property}`, newSelectedValue);
      } else {
        // If selectedValue is null, clear the value
        setValue(`statusesTransitions.${index}.${property}`, null);
      }

      // Trigger validation for the updated field
      await trigger(`statusesTransitions.${index}.${property}`);
    };

    const handleDeleteTaskStatus = async (indexToDelete: number) => {
      // Remove the task status at the specified index
      const updatedTaskStatuses = statusesTransitions.filter(
        (_, index) => index !== indexToDelete
      );

      // Update the form values with the modified array
      setValue('statusesTransitions', updatedTaskStatuses);
      setUnsavedChanges(true);

      // Re-trigger validation for the updated statusesTransitions
      await trigger('statusesTransitions');
    };

    return (
      <FormProvider {...methods}>
        <div className="dialog-tabless-card">
          <Toast ref={toast} />
          <div className="flex flex-column pb-0">
            <div className="dialog-tabless-card__header-gap flex flex-column">
              <Divider />
              <div className="business-processes-constructor-card">
                <div className="flex flex-row gap-5">
                  <div className="business-processes-constructor-card__row w-full">
                    <div className=" flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label className="col-2" htmlFor="name">
                        Название процесса<span className="required">*</span>:
                      </label>
                      <InputText
                        className="oneline-input--grid user-card__input col"
                        {...register('name', {
                          onChange: (e) => handleCurrentInputChange()
                        })}
                      />
                    </div>
                    <p
                      className={`card-errors ${
                        errors.name ? 'text-red-500' : 'text-transparent'
                      } `}
                    >
                      {errors.name ? errors.name.message : ' '}
                    </p>
                  </div>
                </div>
                <div className="flex flex-row gap-5">
                  <div className=" w-full">
                    <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                      <label className="col-2" htmlFor="description">
                        Описание:
                      </label>
                      <InputText
                        className="oneline-input--grid user-card__input col"
                        {...register('description', {
                          onChange: (e) => handleCurrentInputChange()
                        })}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-row gap-5" style={{marginTop: 16}}>
                  <div className=" w-full">
                    <div className="flex flex-row align-items-center gap-3 w-full">
                      <label className="col-2" htmlFor="description">
                        Закрывающий статус<span className="required">*</span>:

                      </label>
                      
                      <Dropdown
                        value={selectedClosingTaskStatuses}  
                        options={statuses}
                        style={{width: 300}}
                        optionLabel="label"
                        showClear
                        placeholder="Выберите закрывающий статус"
                        onChange={handleClosingStatusChange}
                        filter
                        valueTemplate={(option) => (
                          <DropdownWithDeletedValueTemplate 
                            option={option} 
                            selectedItem={selectedClosingTaskStatuses}
                            placeholder={'Выберите закрывающий статус'}
                          />
                        )}
                      />
                      <i 
                        className="pi pi-question-circle closing-status-tooltip" 
                        style={{ fontSize: '1rem', color: '#6c757d', cursor: 'pointer'}}
                        data-pr-tooltip="В выбранном статусе наряд будет считаться закрытым"
                      />
                      <Tooltip target=".closing-status-tooltip" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Divider />
            <div>
              <div className="flex justify-content-between align-items-center gap-3">
                <label htmlFor="department">
                  Добавьте необходимые переходы статусов нарядов
                  <span className="required">*</span>:
                </label>
              </div>
            </div>

            <div
              className="scrollable-container"
              id="tags-scroll"
              style={{
                width: '100%',
                height: '40vh',
                overflow: 'auto'
              }}
            >
              <div
                className="flex flex-column scrollable-content"
                style={{ width: '99%' }}
              >
                {statusesTransitions &&
                  statusesTransitions.map((statusTransition, index) => (
                    <TaskStatusComponent
                      setValue={setValue}
                      statuses={statuses}
                      key={statusTransition.id ?? statusTransition.guid}
                      taskStatus={statusTransition}
                      passedClassName="dialog-card__row"
                      onSelectionChange={handleSelectionChange}
                      onInputChange={(value, property) =>
                        handleInputChange(
                          `statusesTransitions.${index}.${property}` as any,
                          value,
                          index
                        )
                      }
                      onDropdownChange={(selectedValue, property) =>
                        handleDropdownChange(selectedValue, property, index)
                      }
                      onDelete={() => handleDeleteTaskStatus(index)}
                      statusTransition={statusTransition}
                      onTaskStatusUpdate={handleTaskStatusUpdate}
                      // @ts-ignore
                      errors={
                        errors.statusesTransitions
                          ? errors.statusesTransitions[index]
                          : undefined
                      } // Pass errors
                    />
                  ))}
                <div
                  className="flex justify-content-end dialog-card__row"
                  style={{ width: '100%' }}
                >
                  <Button
                    className="align-self-end add-button"
                    icon="pi pi-plus"
                    severity="success"
                    onClick={handleAddTaskStatus}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="default-card__buttons">
            <div className="flex gap-3">
              <Button
                label="Сохранить"
                icon="pi pi-check"
                onClick={handleSubmit(onSubmit, onError)}
              />
              {!withoutButtons && (
                <>
                  <Button
                    label="Удалить"
                    icon="pi pi-trash"
                    onClick={handleDelete}
                    className="p-button-danger"
                  />
                  <Button
                    label="Отмена"
                    icon="pi pi-times"
                    onClick={handleCancel}
                    className="p-button-secondary"
                  />
                </>
              )}
            </div>
          </div>

          <ConfirmationDialog
            visible={showDialog}
            onCancel={handleConfirmationCancel}
            onContinue={handleConfirmationContinue}
          />

          <DeleteConfirmationDialog
            visible={showDeleteDialog}
            onHide={handleDeleteCancellation}
            onConfirm={handleDeleteConfirmation}
          >
            <div>
              Вы уверены, что хотите удалить процесс обработки наряда {name}?
              <br /> Данную операцию невозможно отменить.
            </div>
          </DeleteConfirmationDialog>
        </div>
      </FormProvider>
    );
  }
);

ProcessingWorkOrdersConstructor.displayName = 'ProcessingWorkOrdersConstructor';

export default ProcessingWorkOrdersConstructor;
